import { observable, IObservableArray, computed, IObservableValue, action } from "mobx";
import { ModelItem, IMessageSender } from "..";
import { internalMessageBus, InternalMessageBusChannels, InternalMessageBus, InternalMessageBusMessage } from "sb-resp-lib";
import { defaultItemForSportMode, itemIDs } from "../../modes";

export interface IStateView<T extends ModelItem> {
    readonly SelectedItemId: string;
    readonly Items: IObservableArray<T>;
    selectItem(id: T):void;
    isEventView: boolean;
}

interface IStateActions<T extends ModelItem> {
    add(item: T): void;
    remove(id: string, canUpdateSelectedItem?: boolean): void;
    update(id: string, newValue: Partial<T>, canUpdateSelectedItem?: boolean): void;
    getItemById(id: string): T | undefined;
    setInitialItem(itemId: string): void;
    getSelectedItem(): T | undefined;
    selectInitial():void;
    replaceItems(newItems: T[], canUpdateSelectedItem?: boolean): void;
    clear(): void;
    activate(): void;
    deactivate(): void;
    readonly Items: IObservableArray<T>;
}

export type IState<T extends ModelItem> = IStateView<T> & IStateActions<T>;
type changedToSideMessage = {changedToSide:boolean};

export class State<T extends ModelItem> implements IState<T> {
    private selectedItemId: IObservableValue<string>;
    private readonly items: IObservableArray<T>;
    private readonly messageSender: IMessageSender;
    private initialItemId?: string;
    private defaultItemType : defaultItemForSportMode;

    @observable isEventView: boolean;
    blockId: string;
    internalMessageBus: InternalMessageBus;

    constructor(messageSender: IMessageSender, blockId: string, itemType = defaultItemForSportMode.firstSport) {
        this.messageSender = messageSender;
        this.selectedItemId = observable.box("");
        this.isEventView = false;
        this.items = observable([]);
        this.blockId = blockId;
        this.internalMessageBus = internalMessageBus;
        this.defaultItemType = itemType;
    }

    activate() {
        this.subscribeToToggleEventView(this.blockId, InternalMessageBusChannels.legacy.eventCard.changedToSide);
    }

    deactivate() {
        this.unsubscribeToToggleEventView(this.blockId, InternalMessageBusChannels.legacy.eventCard.changedToSide);
        this.isEventView = false;
    }

    @computed get SelectedItemId(): string {
        return this.selectedItemId.get();
    }

    get Items(): IObservableArray<T> {
        return this.items;
    }

    add(item: T) {
        this.addToList(item, this.Items);
    }

    private addToList(item: T, items: T[]) {
        let indexToInsert = items.findIndex(x => x.order > item.order);
        if (indexToInsert === -1)
        {
            items.push(item);
        }
        else {
            items.splice(indexToInsert, 0, item);
        }
    }

    remove(id: string, canUpdateSelectedItem: boolean = false) {
        const selectedItemId = this.selectedItemId.get();
        if(!canUpdateSelectedItem && id === selectedItemId) {
            return;
        }

        const itemToRemove = this.getItemById(id);
        if (itemToRemove)
        {
            this.items.remove(itemToRemove);
            if (this.items.length > 0 && id === selectedItemId) {
                this.selectItem(this.items[0]);
            }
        }
    }

    update(id: string, newValue: Partial<T>, canUpdateSelectedItem: boolean = false) {
        if (canUpdateSelectedItem || this.selectedItemId.get() !== id) {
            this.updateItem(id, newValue);
        }
    }

    private updateItem(id: string, newValue: Partial<T>) {
        const item = this.getItemById(id);
        item && item.update(newValue);
    }

    getItemById(id: string) {
        return this.items.find(x => x.id === id);
    }

    getSelectedItem() {
        return this.getItemById(this.selectedItemId.get());
    }

    replaceItems(newItems: T[], canUpdateSelectedItem: boolean = false) {
        const selectedItemId = this.selectedItemId.get();
        if (!canUpdateSelectedItem && newItems.length > 0 && newItems.find((item) => item.id === selectedItemId)) {
            newItems = this.getNewItemsWithoutChangingSelected(newItems, selectedItemId);
        }

        this.items.replace(newItems.sort((a, b) => a.order - b.order));
        this.resetSelectedItemId();
    }

    private resetSelectedItemId() {
        const selectedItemId = this.selectedItemId.get();
        if (this.items.length > 0 && selectedItemId && !this.getItemById(selectedItemId)) {
            this.selectItem(this.items[0]);
        }
    }

    selectInitial() {
        const item = this.getInitialItem();
        if(item) {
            this.initialItemId = undefined;
            this.selectedItemId.set("");
            item && this.selectItem(item);
        }
    }

    private getInitialItem() {
        if (this.initialItemId) {
            const item = this.getItemById(this.initialItemId);
            if (item) {
                return item;
            }
        }
        const selectedItem = this.getSelectedItem();
        if (selectedItem) {
            return selectedItem;
        }
        return this.getItemBySportType(this.defaultItemType);
    }

    private getItemBySportType(itemType: defaultItemForSportMode):  T | undefined {
        var item:  T | undefined = undefined;
        switch (itemType) {
            case defaultItemForSportMode.favorites:
                item = this.getItemById(itemIDs.favorites);
                break;
            case defaultItemForSportMode.liveStreaming:
                item = this.getItemById(itemIDs.liveStreaming);
                break;
        }
        if (!item) {
            item = this.getFirstSportItem();
        }
        return item;
    }

    private getFirstSportItem() : T | undefined {
        for (let item of this.items) {
            if (item.order >= 0) {
                return item;
            }
        }
        return undefined;
    }

    setInitialItem(itemId: string) {
        this.initialItemId = itemId;
        this.selectedItemId.set("");
    }

    private getNewItemsWithoutChangingSelected(newItems: T[], selectedItemId: string) {
        const selectedItem = this.getSelectedItem();
        if (selectedItem) {
            newItems = newItems.filter(x => selectedItemId !== x.id);
            this.addToList(selectedItem, newItems);
        }
        return newItems;
    }

    selectItem(item: T) {
        if (this.selectedItemId.get() === item.id)
        {
            return;
        }
        this.messageSender.SendMessage(item);
        this.selectedItemId.set(item.id);
    }

    clear() {
        this.items.replace([]);
    }

    subscribeToToggleEventView(BlockUniqueID: string, channel: string) {
        this.internalMessageBus.on<changedToSideMessage>(`${BlockUniqueID}.${channel}`, this.toggleEventView, this);
    }

    unsubscribeToToggleEventView(BlockUniqueID: string, channel: string) {
        this.internalMessageBus.off<changedToSideMessage>(`${BlockUniqueID}.${channel}`, this.toggleEventView, this);
    }

    @action.bound toggleEventView(topic: InternalMessageBusMessage<changedToSideMessage>) {
        topic.data && topic.data[0] && (this.isEventView = topic.data[0].changedToSide);
    }
}